import { useEffect, useState } from "react";
import { resolveColourScheme, type ColourScheme, type WidgetTheme } from "../../utils/widgetTheme";

function prefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
}

/**
 * Resolves the widget's `theme` setting to the colour scheme actually painted.
 * `light` and `dark` are returned unchanged; `auto` follows the browser's
 * `prefers-color-scheme` and re-resolves live when the guest's OS scheme flips
 * (the listener is only subscribed for `auto`).
 */
export function useWidgetColourScheme(theme: WidgetTheme): ColourScheme {
  const [scheme, setScheme] = useState<ColourScheme>(() => resolveColourScheme(theme, prefersDark()));

  useEffect(() => {
    if (theme !== "auto" || typeof window.matchMedia !== "function") {
      setScheme(resolveColourScheme(theme, prefersDark()));
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => {
      setScheme(resolveColourScheme(theme, event.matches));
    };

    setScheme(resolveColourScheme(theme, mediaQuery.matches));
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, [theme]);

  return scheme;
}
